import { getFirestore } from '../../system/firebase';

import { SNACK_BAR_MESSAGE_OPERATION_FAILED, SNACK_BAR_MESSAGE_SAVE } from '../constants';
import { convertFirebaseTimestampsToDate } from '../dateHelper';
import { SORT_OPTION_NEWEST_FIRST } from '../constants';
import {
    ELASTICSEARCH_PAGE_SIZE,
    ELASTICSEARCH_SORT_OPTIONS,
    ELASTICSEARCH_SORT_OPTION_APPLY_DATE_DESC,
    ELASTICSEARCH_SORT_OPTION_UPDATE_DATE_DESC,
    mapJsAppliedJobsDataFromElasticSearchJson,
    mapJsSavedJobsDataFromElasticSearchJson,
} from '../elasticSearchHelper';
import { mapDefinedFields } from '../dtoHelper';
import { checkEsToken, elasticSearchJsAppliedJobSearchPostFetch, elasticSearchJsSavedJobSearchPostFetch } from './appServices';

import _get from 'lodash/get';
// import _set from 'lodash/set';
import _map from 'lodash/map';
import _omit from 'lodash/omit';

export const loadMyJobsByUserId = ({ userId, setData }) => {
    const db = getFirestore();

    // const dashboardsRef = db.collection('dashboards').where('jobSeekerId', '==', userId);
    const myJobsRef = db.collection('myJobs').doc(userId);

    return myJobsRef.onSnapshot(docSnapshot => {
        // expect only one document per user
        setData(docSnapshot.data());
    });
};

export const fetchJsSavedJobs = async ({ from = 0, size = ELASTICSEARCH_PAGE_SIZE, queryObject, esToken, setEsToken } = {}) => {
    // format the search criteria queryObject
    const sortDto = queryObject?.sortTag || ELASTICSEARCH_SORT_OPTIONS[ELASTICSEARCH_SORT_OPTION_UPDATE_DATE_DESC];

    // Create the actual queryObject for ElasticSearch
    const mappedQueryObject = mapDefinedFields({
        parentId: queryObject?.parentId || undefined,
        jobTitle: queryObject?.searchText || undefined,
        ...sortDto,
    });
    // console.log('==========> ', { mappedQueryObject });

    let checkedEsToken = null;
    try {
        checkedEsToken = await checkEsToken(esToken);

        if (checkedEsToken) {
            const searchResult = await elasticSearchJsSavedJobSearchPostFetch({ queryObject: { ...mappedQueryObject, from, size }, token: checkedEsToken.token });

            if (_get(searchResult, 'success')) {
                const total = _get(searchResult, 'total', 0);
                const result = _get(searchResult, 'result', []);
                const mappedList = _map(result, item => {
                    return mapJsSavedJobsDataFromElasticSearchJson(item);
                });
                return { total, list: mappedList };
            } else {
                console.error('Failed to search JS saved jobs');
            }

            if (checkedEsToken.isNew) {
                setEsToken(_omit(checkedEsToken, 'isNew'));
            }
        }
    } catch (error) {
        console.error('Failed checking token', error);
    }

    return { total: 0, list: [] };
};

export const fetchJsAppliedJobs = async ({ from = 0, size = ELASTICSEARCH_PAGE_SIZE, queryObject, esToken, setEsToken } = {}) => {
    // format the search criteria queryObject
    const sortDto = queryObject?.sortTag || ELASTICSEARCH_SORT_OPTIONS[ELASTICSEARCH_SORT_OPTION_APPLY_DATE_DESC];

    // Create the actual queryObject for ElasticSearch
    const mappedQueryObject = mapDefinedFields({
        parentId: queryObject?.parentId || undefined,
        jobTitle: queryObject?.searchText || undefined,
        ...sortDto,
    });
    // console.log('==========> ', { mappedQueryObject });

    let checkedEsToken = null;
    try {
        checkedEsToken = await checkEsToken(esToken);

        if (checkedEsToken) {
            const searchResult = await elasticSearchJsAppliedJobSearchPostFetch({ queryObject: { ...mappedQueryObject, from, size }, token: checkedEsToken.token });

            if (_get(searchResult, 'success')) {
                const total = _get(searchResult, 'total', 0);
                const result = _get(searchResult, 'result', []);
                const mappedList = _map(result, item => {
                    return mapJsAppliedJobsDataFromElasticSearchJson(item);
                });
                return { total, list: mappedList };
            } else {
                console.error('Failed to search JS applied jobs');
            }

            if (checkedEsToken.isNew) {
                setEsToken(_omit(checkedEsToken, 'isNew'));
            }
        }
    } catch (error) {
        console.error('Failed checking token', error);
    }

    return { total: 0, list: [] };
};

export const loadSavedJobsByUserId = ({ userId, setData, sortOrder = SORT_OPTION_NEWEST_FIRST }) => {
    const db = getFirestore();
    const order = sortOrder === SORT_OPTION_NEWEST_FIRST ? 'desc' : undefined;

    const jsSavedJobsQuery = db.collection('myJobs').doc(userId).collection('jsSavedJobs').orderBy('statusDt', order);

    return jsSavedJobsQuery.onSnapshot(querySnapshot => {
        const savedJobs = [];
        querySnapshot.forEach(async doc => {
            const data = convertFirebaseTimestampsToDate(doc.data(), ['activeDt', 'inactiveDt', 'applyDt', 'statusDt', 'createDt', 'updateDt']);
            savedJobs.push(data);
        });
        setData(savedJobs);
    });
};

export const loadAppliedJobsByUserId = ({ userId, setData, sortOrder = SORT_OPTION_NEWEST_FIRST }) => {
    const db = getFirestore();
    const order = sortOrder === SORT_OPTION_NEWEST_FIRST ? 'desc' : undefined;

    const appliedJobsQuery = db.collection('myJobs').doc(userId).collection('appliedJobs').orderBy('applyDt', order);

    return appliedJobsQuery.onSnapshot(querySnapshot => {
        const appliedJobs = [];
        querySnapshot.forEach(async doc => {
            const data = convertFirebaseTimestampsToDate(doc.data(), ['activeDt', 'inactiveDt', 'applyDt', 'statusDt', 'createDt', 'updateDt']);
            appliedJobs.push(data);
        });
        setData(appliedJobs);
    });
};

export const saveMyJobsNote = async ({ userId, jobId, note = '', isSavedJob, setIsNoteEnabled, setSnackbarMessage, setSnackbarErrorMessage }) => {
    // const db = firebase.firestore();
    const db = getFirestore();

    const saveJobRef = db
        .collection('myJobs')
        .doc(userId)
        .collection(isSavedJob ? 'jsSavedJobs' : 'appliedJobs')
        .doc(jobId);

    try {
        await saveJobRef.set({ note }, { merge: true });

        // console.log('Transaction successfully committed!');
        setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
        setIsNoteEnabled && setIsNoteEnabled(false);

        return true;
    } catch (error) {
        console.error('Save note transaction failed: ', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);

        return false;
    }
};

export const fetchJob = async ({ userId, jobId, isAppliedView, setData, setSnackbarErrorMessage }) => {
    const db = getFirestore();
    const jobRef = db.collection(`myJobs/${userId}/${isAppliedView ? 'appliedJobs' : 'jsSavedJobs'}`).doc(jobId);

    try {
        const doc = await jobRef.get();
        setData({ ...convertFirebaseTimestampsToDate({ ...doc.data(), uid: doc.id }, ['activeDt', 'inactiveDt', 'applyDt', 'statusDt', 'createDt', 'updateDt']) });
    } catch (error) {
        console.log('Failed to retrieve job record', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
    }
};
